import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from '../icons';
import { useMediaQuery } from '../../hooks';
import { Select } from '../primitives';

export interface ProjectNavItem {
  id: string;
  label: string;
  /** Rendered as a small count next to the label; zero is not shown. */
  count?: number;
  /** Progress through the section's steps, e.g. "5/7" (setup). */
  progress?: string;
}

/**
 * ProjectNav is the project's section navigation (CO-150). Real links: each
 * section is its own URL, so it can be bookmarked and reached with Back.
 *
 * - Every section is a tab: there is room for all of them beside the
 *   sidebar, and a hidden "More" only cost a click and hid where you are.
 * - If the tabs do not fit (a narrow tablet, 200% zoom) the strip scrolls,
 *   shows an edge fade only on the side with more, offers arrow buttons,
 *   and scrolls the current tab into view.
 * - On a phone it is one labelled "Section" selector listing every section.
 */
export function ProjectNav({
  items,
  basePath,
  search,
  currentId,
}: {
  items: ProjectNavItem[];
  basePath: string;
  /** The environment context, carried onto every section link. */
  search: string;
  currentId: string;
}) {
  const phone = useMediaQuery('(max-width: 767.98px)');
  const navigate = useNavigate();
  const pathOf = (id: string) => `${basePath}/${id}${search}`;
  const labelOf = (item: ProjectNavItem) => `${item.label}${item.progress ? `, ${item.progress} steps done` : ''}${item.count ? `, ${item.count}` : ''}`;

  if (phone) {
    return (
      <nav className="project-nav project-nav--select" aria-label="Project sections">
        <label className="project-nav__select-label" htmlFor="project-section-select">
          Section
        </label>
        <span className="project-nav__select-wrap">
          <Select id="project-section-select" value={currentId} onChange={(event) => navigate(pathOf(event.target.value))}>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {labelOf(item)}
              </option>
            ))}
          </Select>
          <Icon name="chevron-down" size={16} />
        </span>
      </nav>
    );
  }

  return (
    <nav className="project-nav" aria-label="Project sections">
      <ScrollStrip currentId={currentId}>
        {items.map((item) => (
          <NavLink
            key={item.id}
            to={{ pathname: `${basePath}/${item.id}`, search }}
            data-section={item.id}
            className={({ isActive }) => `project-nav__link${isActive ? ' project-nav__link--active' : ''}`}
            aria-label={labelOf(item)}
          >
            <span>{item.label}</span>
            {item.progress ? <span className="project-nav__count tabular-nums">{item.progress}</span> : null}
            {typeof item.count === 'number' && item.count > 0 ? <span className="project-nav__count tabular-nums">{item.count}</span> : null}
          </NavLink>
        ))}
      </ScrollStrip>
    </nav>
  );
}

/** A horizontal strip that says when it holds more than it shows. */
function ScrollStrip({ children, currentId }: { children: React.ReactNode; currentId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const currentRef = useRef(currentId);
  currentRef.current = currentId;

  // The current section is always in view, clear of the edge fade: when it
  // changes and when the strip's width changes (a resized window).
  const reveal = () => {
    const strip = ref.current;
    const current = strip?.querySelector<HTMLElement>(`[data-section="${currentRef.current}"]`);
    if (!strip || !current) return;
    const fade = 40;
    const left = current.offsetLeft - strip.offsetLeft;
    const right = left + current.offsetWidth;
    if (left < strip.scrollLeft + (strip.scrollLeft > 1 ? fade : 0)) {
      strip.scrollTo({ left: Math.max(0, left - fade) });
    } else if (right > strip.scrollLeft + strip.clientWidth - (right < strip.scrollWidth - 1 ? fade : 0)) {
      strip.scrollTo({ left: right - strip.clientWidth + fade });
    }
  };

  useEffect(() => {
    const strip = ref.current;
    if (!strip) return;
    const update = () => setEdges({ start: strip.scrollLeft > 1, end: strip.scrollLeft + strip.clientWidth < strip.scrollWidth - 1 });
    update();
    strip.addEventListener('scroll', update, { passive: true });
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => {
            reveal();
            update();
          })
        : null;
    observer?.observe(strip);
    return () => {
      strip.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, []);

  useEffect(reveal, [currentId]);

  const scrollBy = (direction: -1 | 1) => ref.current?.scrollBy({ left: direction * Math.max(160, (ref.current?.clientWidth ?? 0) * 0.6), behavior: 'smooth' });
  return (
    <div className={`scroll-strip${edges.start ? ' scroll-strip--start' : ''}${edges.end ? ' scroll-strip--end' : ''}`}>
      {edges.start ? (
        <button type="button" className="scroll-strip__arrow scroll-strip__arrow--start" aria-label="Show earlier sections" onClick={() => scrollBy(-1)}>
          <Icon name="back" size={16} />
        </button>
      ) : null}
      <div className="scroll-strip__track" ref={ref}>
        {children}
      </div>
      {edges.end ? (
        <button type="button" className="scroll-strip__arrow scroll-strip__arrow--end" aria-label="Show more sections" onClick={() => scrollBy(1)}>
          <Icon name="forward" size={16} />
        </button>
      ) : null}
    </div>
  );
}
